import type { PageableRangeInput } from '@allocab/allocab-extranet-api-types';
import type { ListOutput } from '@allocab/allocab-standard-shared-types';
import { useQuery } from '@tanstack/react-query';

import type {
  DashboardRateInput,
  ExtranetIncidentBackCategory,
  ExtranetIncidentOutput,
  IncidentCategoryBucket,
} from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toPageableRangeInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

interface IncidentCategoryBucketsResponse {
  buckets: IncidentCategoryBucket[];
}

/**
 * Le back n'expose plus `/v1/dashboard/incident-categories`. On dérive les
 * buckets côté front à partir de la liste des incidents sur la période —
 * coût acceptable tant que le volume reste raisonnable (limit max). À retirer
 * si Yohan livre une route d'agrégation dédiée.
 *
 * Mapping vers la taxonomie front (6 cat de la spec §8.4) depuis les 4 cat
 * livrées par le back : security, driver_behavior → security ;
 * technical → communication ; other → communication.
 */
const BACK_TO_FRONT_CATEGORY: Record<ExtranetIncidentBackCategory, IncidentCategoryBucket['category']> = {
  security: 'security',
  driver_behavior: 'security', // sécurité élargie (RG-INC-2)
  technical: 'communication',
  other: 'communication',
};

export const useIncidentCategoryBuckets = (input: DashboardRateInput) =>
  useQuery<IncidentCategoryBucketsResponse>({
    queryKey: extranetQueryKeys.dashboard.incidentCategoryBuckets(input),
    queryFn: async () => {
      const list = await getExtranetApi().get<PageableRangeInput, ListOutput<ExtranetIncidentOutput>>(
        '/v1/incidents/',
        toPageableRangeInput(input, { offset: 0, limit: 500 }),
      );

      const counts = new Map<IncidentCategoryBucket['category'], number>();
      for (const item of list.items ?? []) {
        const mapped = BACK_TO_FRONT_CATEGORY[item.category] ?? 'communication';
        counts.set(mapped, (counts.get(mapped) ?? 0) + 1);
      }
      const buckets: IncidentCategoryBucket[] = [
        'pickup_delay',
        'driver_absent',
        'security',
        'vehicle',
        'agent_pickup',
        'communication',
      ].map(category => ({
        category: category as IncidentCategoryBucket['category'],
        count: counts.get(category as IncidentCategoryBucket['category']) ?? 0,
      }));
      return { buckets };
    },
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });
